/**
 * Deterministic per-group color assignment for the calendar.
 *
 * All classes are static strings so Tailwind's JIT compiler sees them.
 * The palette deliberately avoids flock-navy (reserved for app chrome) and
 * keeps 100-level backgrounds with 700-level text for AA contrast.
 */

export interface GroupColor {
  /** Chip background, e.g. event chips on the month grid. */
  chipBg: string;
  /** Text color paired with chipBg. */
  chipText: string;
  /** Small solid dot / legend swatch. */
  dot: string;
  /** Border accent. */
  border: string;
  /** Solid background for all-day chips and pills. */
  solidBg: string;
  /** Text on solidBg. */
  solidText: string;
}

const GROUP_PALETTE: GroupColor[] = [
  { chipBg: 'bg-sky-100', chipText: 'text-sky-800', dot: 'bg-sky-500', border: 'border-sky-300', solidBg: 'bg-sky-600', solidText: 'text-white' },
  { chipBg: 'bg-emerald-100', chipText: 'text-emerald-800', dot: 'bg-emerald-500', border: 'border-emerald-300', solidBg: 'bg-emerald-600', solidText: 'text-white' },
  { chipBg: 'bg-amber-100', chipText: 'text-amber-800', dot: 'bg-amber-500', border: 'border-amber-300', solidBg: 'bg-amber-600', solidText: 'text-white' },
  { chipBg: 'bg-violet-100', chipText: 'text-violet-800', dot: 'bg-violet-500', border: 'border-violet-300', solidBg: 'bg-violet-600', solidText: 'text-white' },
  { chipBg: 'bg-rose-100', chipText: 'text-rose-800', dot: 'bg-rose-500', border: 'border-rose-300', solidBg: 'bg-rose-600', solidText: 'text-white' },
  { chipBg: 'bg-teal-100', chipText: 'text-teal-800', dot: 'bg-teal-500', border: 'border-teal-300', solidBg: 'bg-teal-600', solidText: 'text-white' },
  { chipBg: 'bg-indigo-100', chipText: 'text-indigo-800', dot: 'bg-indigo-500', border: 'border-indigo-300', solidBg: 'bg-indigo-600', solidText: 'text-white' },
  { chipBg: 'bg-orange-100', chipText: 'text-orange-800', dot: 'bg-orange-500', border: 'border-orange-300', solidBg: 'bg-orange-600', solidText: 'text-white' },
];

/** Stable hash of a UUID so a group keeps its color across sessions. */
export function getGroupColor(groupId: string): GroupColor {
  let hash = 0;
  for (let i = 0; i < groupId.length; i++) {
    hash = (hash * 31 + groupId.charCodeAt(i)) | 0;
  }
  return GROUP_PALETTE[Math.abs(hash) % GROUP_PALETTE.length];
}
